import { useEffect, useCallback } from "react";
import { CARD_STATUS_CODES } from "@/features/card/configs/card.config";
import { updateCardStatus } from "@/features/card/hooks/useCardMutations";
import { useDependencies } from "@/shared/context/DependenciesContext";
import { usePush } from "@/shared/hooks/usePush";
import { useUser } from "@/shared/hooks/useUser";
import type { CardEventData } from "@/shared/types/events.type";

export const useCardEvents = (currentCardId: string, onPusherResponse?: (success: boolean) => void): null => {
  const { subscribe, unsubscribe } = usePush();
  const { id: userId } = useUser();
  const { queryClient } = useDependencies();

  const onLockEvent = useCallback(async ({ card_id, success }: CardEventData): Promise<void> => {
    if (card_id !== currentCardId) return;
    // Update cache only on success - multiple calls with same data is idempotent
    if (success) {
      updateCardStatus(queryClient, card_id, CARD_STATUS_CODES.LOCK);
    }
    // Callback will update local UI state
    onPusherResponse?.(success);
  }, [currentCardId, onPusherResponse, queryClient]);

  const onUnlockEvent = useCallback(async ({ card_id, success }: CardEventData): Promise<void> => {
    if (card_id !== currentCardId) return;
    // Update cache only on success - multiple calls with same data is idempotent
    if (success) {
      updateCardStatus(queryClient, card_id, CARD_STATUS_CODES.UNLOCK);
    }
    // Callback will update local UI state
    onPusherResponse?.(success);
  }, [currentCardId, onPusherResponse, queryClient]);

  const onActivateEvent = useCallback(async ({ card_id, success }: CardEventData): Promise<void> => {
    if (card_id !== currentCardId) return;
    // For activation, refetch to get full updated card data
    if (success) {
      await queryClient.refetchQueries({ queryKey: ["cards"], type: "all" });
    }
    onPusherResponse?.(success);
  }, [currentCardId, onPusherResponse, queryClient]);

  useEffect(() => {
    if (!userId) return;

    subscribe(userId, "card_lock", onLockEvent);
    subscribe(userId, "card_unlock", onUnlockEvent);
    subscribe(userId, "card_activate", onActivateEvent);

    return () => {
      unsubscribe(userId, "card_lock", onLockEvent);
      unsubscribe(userId, "card_unlock", onUnlockEvent);
      unsubscribe(userId, "card_activate", onActivateEvent);
    };
  }, [userId, onLockEvent, onUnlockEvent, onActivateEvent, subscribe, unsubscribe]);

  return null;
};
